import { formatWhen } from '../../lib/display-time'
/**
 * My day (scope §5.11): cases I own, due today, overdue first. Pure functions so the ordering rules are
 * unit-tested without a DOM. Owner: web-phone-pwa.
 */
import type { CaseRow, LaneResult, MyDayLane, TicketChip } from './api'

export type Bucket = 'overdue' | 'today' | 'later'

export interface DayItem {
  row: CaseRow
  bucket: Bucket
  due: Date | null
}

export interface MyDay {
  overdue: DayItem[]
  today: DayItem[]
  later: DayItem[]
  /** Lanes that failed: their cases are unknown, so the day is incomplete (never "nothing due"). */
  unreachable: { lane: string; error: string }[]
  /** Oldest server Date among the lanes that answered; a service-worker copy keeps its old Date. */
  servedAt: number | null
}

export interface TakenLaneCase {
  lane: MyDayLane
  row: CaseRow
}

/** Optimistic Take: remove one case from the unassigned lane set and remember exactly where it came from. */
export function removeLaneCase(
  lanes: LaneResult[] | undefined,
  caseId: string,
): { lanes: LaneResult[] | undefined; taken?: TakenLaneCase } {
  let taken: TakenLaneCase | undefined
  const next = lanes?.map((lane) => {
    const row = lane.rows?.find((candidate) => candidate.id === caseId)
    if (!row) return lane
    taken = { row, lane: lane.lane }
    return { ...lane, rows: lane.rows?.filter((candidate) => candidate.id !== caseId) ?? null }
  })
  return { lanes: next, taken }
}

/** Optimistic Take: add the case to the same work lane in the new owner's cache. */
export function addLaneCase(lanes: LaneResult[] | undefined, taken: TakenLaneCase | undefined, row = taken?.row): LaneResult[] | undefined {
  if (!lanes || !taken || !row) return lanes
  return lanes.map((lane) => {
    if (lane.lane !== taken.lane || !lane.rows) return lane
    const existing = lane.rows.some((candidate) => candidate.id === row.id)
    return {
      ...lane,
      rows: existing ? lane.rows.map((candidate) => (candidate.id === row.id ? row : candidate)) : [row, ...lane.rows],
    }
  })
}

function endOfDay(now: Date): Date {
  const d = new Date(now)
  d.setHours(23, 59, 59, 999)
  return d
}

export function bucketOf(row: CaseRow, now: Date): { bucket: Bucket; due: Date | null } {
  const raw = row.next_move?.due_at
  const due = raw ? new Date(raw) : null
  const validDue = due && !Number.isNaN(due.getTime()) ? due : null
  if (row.next_move?.overdue || (validDue && validDue.getTime() < now.getTime())) return { bucket: 'overdue', due: validDue }
  if (validDue && validDue.getTime() <= endOfDay(now).getTime()) return { bucket: 'today', due: validDue }
  return { bucket: 'later', due: validDue }
}

const byDue = (a: DayItem, b: DayItem) => {
  const ta = a.due?.getTime() ?? Number.POSITIVE_INFINITY
  const tb = b.due?.getTime() ?? Number.POSITIVE_INFINITY
  return ta - tb || a.row.number.localeCompare(b.row.number)
}

export function buildMyDay(lanes: LaneResult[], ownerId: string, now: Date = new Date()): MyDay {
  const seen = new Map<string, CaseRow>()
  const unreachable: MyDay['unreachable'] = []
  let servedAt: number | null = null
  for (const l of lanes) {
    if (!l.rows) {
      unreachable.push({ lane: l.lane, error: l.error ?? 'unreachable' })
      continue
    }
    if (l.servedAt !== null) servedAt = servedAt === null ? l.servedAt : Math.min(servedAt, l.servedAt)
    for (const r of l.rows) {
      if (r.conversation_state === 'closed') continue
      // The API filters by owner; keep the check so a mis-filtered or unowned row cannot enter "My day".
      if (r.owner?.id !== ownerId) continue
      if (!seen.has(r.id)) seen.set(r.id, r)
    }
  }
  const out: MyDay = { overdue: [], today: [], later: [], unreachable, servedAt }
  for (const row of seen.values()) {
    const { bucket, due } = bucketOf(row, now)
    out[bucket].push({ row, bucket, due })
  }
  out.overdue.sort(byDue)
  out.today.sort(byDue)
  out.later.sort(byDue)
  return out
}

/** The ticket a time entry lands on: the primary ticket, else the first live one. */
export function primaryTicket(tickets: TicketChip[]): TicketChip | null {
  const live = tickets.filter((t) => !t.tombstoned)
  return live.find((t) => t.role === 'primary') ?? live[0] ?? null
}

export function formatDue(due: Date | null, now: Date = new Date()): string {
  if (!due) return 'No due time'
  const time = formatWhen(due.toISOString(), { style: 'time' })
  const sameDay = due.toDateString() === now.toDateString()
  if (sameDay) return time
  const day = formatWhen(due.toISOString(), { style: 'date' })
  return `${day} ${time}`
}

/** Next working day at `hour` local (Sat/Sun skip to Monday). */
export function nextWorkingDay(now: Date, hour: number): Date {
  const d = new Date(now)
  d.setDate(d.getDate() + 1)
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1)
  d.setHours(hour, 0, 0, 0)
  return d
}

export function todayAt(now: Date, hour: number): Date {
  const d = new Date(now)
  d.setHours(hour, 0, 0, 0)
  return d
}

/** "Saved copy from 10:42" when the data is older than two minutes (a service-worker answer offline). */
export function staleLabel(servedAt: number | null, now: number = Date.now()): string | null {
  if (servedAt === null || now - servedAt < 120_000) return null
  const t = new Date(servedAt)
  return `Saved copy from ${formatWhen(t.toISOString(), { style: 'absolute' })}`
}
